import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldX, Mail, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const i18n = {
  en: {
    backToSite: 'Back to Site',
    title: 'Delete My Data',
    intro: 'Under the GDPR (Article 17 — Right to Erasure), you may request that we permanently delete all personal data we hold about you.',
    whatGetsDeleted: 'What will be permanently deleted',
    items: [
      'Any chat conversations and AI Concierge messages associated with your email',
      'Any leads or contact details you left via the chatbot',
      'Any inquiries submitted through the contact form',
      'Any related chat sessions linked to those records',
    ],
    irreversible: 'This action is irreversible. Once deleted, your data cannot be recovered.',
    emailLabel: 'Your email address',
    confirmLabel: 'I confirm I want to permanently delete all personal data associated with this email.',
    submit: 'Delete my data',
    successTitle: 'Done — your data has been deleted',
    successBody: 'We\'ve permanently deleted the records below. A confirmation email has also been sent to your address for your records.',
    noDataTitle: 'No data found',
    noDataBody: 'We didn\'t find any personal data associated with this email. A confirmation has still been sent so you have it for your records.',
    deleted: {
      chat_leads: 'chat lead(s)',
      chat_messages: 'chat message(s)',
      chat_sessions: 'chat session(s)',
      contact_submissions: 'contact form submission(s)',
    },
    error: 'Something went wrong. Please try again or email us directly at petrosperimenis [at] yahoo [dot] gr.',
    rateLimit: 'Too many deletion requests from your network. Please try again in an hour.',
    note: 'Need help or prefer to email us instead? Write to petrosperimenis [at] yahoo [dot] gr and we\'ll process your request within 30 days as required by GDPR.',
    privacyLink: 'Read our full Privacy Policy',
    backHome: 'Back to home',
  },
};

const DataDeletion = () => {
  const { language } = useLanguage();
  const t = i18n[language] || i18n.en;
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !confirm) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/data-deletion/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), confirm: true }),
      });
      if (res.status === 429) {
        toast.error(t.rateLimit);
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || t.error);
        return;
      }
      const data = await res.json();
      setResult(data);
    } catch (err) {
      toast.error(t.error);
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    const isEmpty = result.total === 0;
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="mb-6 text-slate-600 hover:text-slate-800"
            data-testid="data-deletion-back"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t.backHome}
          </Button>

          <Card className="border-none shadow-xl bg-white">
            <CardContent className="p-8 md:p-10" data-testid="data-deletion-result">
              <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-5 ${isEmpty ? 'bg-slate-100' : 'bg-emerald-100'}`}>
                <CheckCircle2 className={`w-7 h-7 ${isEmpty ? 'text-slate-500' : 'text-emerald-600'}`} />
              </div>
              <h1 className="font-serif text-2xl text-slate-800 mb-3">
                {isEmpty ? t.noDataTitle : t.successTitle}
              </h1>
              <p className="text-slate-600 leading-relaxed mb-5">
                {isEmpty ? t.noDataBody : t.successBody}
              </p>

              {!isEmpty && (
                <ul className="space-y-2 mb-5">
                  {Object.entries(result.deleted).map(([k, n]) => (
                    n > 0 && (
                      <li key={k} className="flex items-center gap-3 text-slate-700">
                        <span className="w-7 h-7 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-sm font-bold">
                          {n}
                        </span>
                        <span className="text-sm">{t.deleted[k] || k}</span>
                      </li>
                    )
                  ))}
                </ul>
              )}

              <Button
                onClick={() => navigate('/')}
                className="bg-[#1e3a5f] hover:bg-[#15293f] text-white"
                data-testid="data-deletion-done-button"
              >
                {t.backHome}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/')}
          className="mb-6 text-slate-600 hover:text-slate-800"
          data-testid="data-deletion-back"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t.backToSite}
        </Button>

        <Card className="border-none shadow-xl bg-white">
          <CardContent className="p-8 md:p-10">
            <div className="w-14 h-14 bg-gradient-to-br from-[#d4845f] to-[#c17552] rounded-full flex items-center justify-center mb-5">
              <ShieldX className="w-7 h-7 text-white" />
            </div>

            <h1 className="font-serif text-2xl md:text-3xl text-slate-800 mb-3" data-testid="data-deletion-title">
              {t.title}
            </h1>
            <p className="text-slate-600 leading-relaxed mb-6">{t.intro}</p>

            <div className="bg-slate-50 rounded-lg p-5 mb-6 border border-slate-200">
              <h2 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">
                {t.whatGetsDeleted}
              </h2>
              <ul className="space-y-2">
                {t.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                    <span className="text-[#d4845f] mt-0.5">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded mb-6 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">{t.irreversible}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="data-deletion-form">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  <Mail className="w-4 h-4 inline mr-1.5 text-slate-400" />
                  {t.emailLabel}
                </label>
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="data-deletion-email-input"
                  className="bg-white"
                />
              </div>

              <label className="flex items-start gap-3 cursor-pointer bg-slate-50 hover:bg-slate-100 transition-colors p-4 rounded-lg border border-slate-200">
                <input
                  type="checkbox"
                  checked={confirm}
                  onChange={(e) => setConfirm(e.target.checked)}
                  data-testid="data-deletion-confirm-checkbox"
                  className="mt-0.5 w-4 h-4 accent-[#d4845f]"
                />
                <span className="text-sm text-slate-700 leading-relaxed">{t.confirmLabel}</span>
              </label>

              <Button
                type="submit"
                disabled={submitting || !email.trim() || !confirm}
                className="w-full bg-[#d4845f] hover:bg-[#c17552] text-white py-6 font-medium"
                data-testid="data-deletion-submit-button"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing…</>
                ) : (
                  <><ShieldX className="w-4 h-4 mr-2" />{t.submit}</>
                )}
              </Button>
            </form>

            <p className="text-xs text-slate-500 mt-6 leading-relaxed">{t.note}</p>
            <button
              type="button"
              onClick={() => navigate('/privacy')}
              className="text-xs text-[#1e3a5f] hover:text-[#d4845f] underline underline-offset-2 mt-3 inline-block"
              data-testid="data-deletion-privacy-link"
            >
              {t.privacyLink} ↗
            </button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DataDeletion;
